import { Text } from '@/components/ui/text';
import i18n from 'i18next';
import { useMemo } from 'react';
import { View } from 'react-native';
import { Icon } from '~/global/components/icon/Icon';
import { format, isSameDay } from 'date-fns';
import { enUS, sk } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export type DatePickerVariant = 'card' | 'pill';

export interface DatePickerProps {
  value: Date;
  onChange: (date: Date) => void;
  /** `card` = full-width form card (default), `pill` = compact header pill. */
  variant?: DatePickerVariant;
  /** Label shown above the value in the `card` variant. */
  label?: string;
  disabled?: boolean;
  /**
   * Earliest selectable date (inclusive). Days before it are disabled. Used to bound
   * Free-tier users to their history window — see `useHistoryMinDate`.
   */
  minimumDate?: Date;
  className?: string;
  /** testID on the trigger surface, so e2e can open a specific picker. */
  testID?: string;
}

const dateFnsLocales: Record<string, Locale> = { en: enUS, sk };

export const dateFnsLocale = (language: string): Locale =>
  dateFnsLocales[language.split('-')[0]] ?? enUS;

export const useDatePickerDisplay = (
  value: Date,
  variant: DatePickerVariant,
) => {
  const { t } = useTranslation('common');
  return useMemo(() => {
    const locale = dateFnsLocale(i18n.language);
    if (variant === 'card' && isSameDay(value, new Date())) {
      return `${t('today')}, ${format(value, 'MMMM d', { locale })}`;
    }
    return format(value, variant === 'pill' ? 'EEEE, MMM d' : 'EEEE, MMMM d', {
      locale,
    });
  }, [value, variant, t]);
};

export interface DatePickerTriggerProps {
  value: Date;
  variant?: DatePickerVariant;
  label?: string;
  className?: string;
  testID?: string;
}

/**
 * Presentational date-picker surface shared by the web (popover) and native
 * (OS picker) implementations. Renders the value only — interaction is owned
 * by the platform-specific `DatePicker`.
 */
export const DatePickerTrigger = ({
  value,
  variant = 'card',
  label,
  className,
  testID,
}: DatePickerTriggerProps) => {
  const { t } = useTranslation('common');
  const displayValue = useDatePickerDisplay(value, variant);

  if (variant === 'pill') {
    return (
      <View
        testID={testID}
        collapsable={false}
        className={cn(
          'flex-row items-center gap-2.5 rounded-[10px] bg-surface px-3.5 py-2',
          className,
        )}
      >
        <Icon name="Calendar" size={18} className="text-text-muted" />
        <Text className="text-sm font-inter-medium text-text-primary">
          {displayValue}
        </Text>
        <Icon name="ChevronDown" size={16} className="text-text-muted" />
      </View>
    );
  }

  return (
    <View
      testID={testID}
      collapsable={false}
      className={cn(
        'flex-row items-center justify-between rounded-xl bg-surface-elevated px-4 py-3',
        className,
      )}
    >
      <View className="flex-row items-center gap-3">
        <Icon name="Calendar" size={20} className="text-accent" />
        <View className="gap-0.5">
          <Text className="text-[11px] font-inter-medium text-text-secondary">
            {label ?? t('date')}
          </Text>
          <Text className="text-sm font-inter-semibold text-text-primary">
            {displayValue}
          </Text>
        </View>
      </View>
      <Icon name="ChevronDown" size={18} className="text-text-secondary" />
    </View>
  );
};
